import { Component, ElementRef, Injector, Input, OnDestroy, OnInit } from '@angular/core';
import { BehaviorSubject, filter, Subscription } from 'rxjs';

import { AbstractDialogComponent } from '../../../../Core/Dialogs/components/AbstractDialog/abstract-dialog.component';
import { AgprComponentStyleEnum } from '../../../../Common/Basic/enums/social-analysis-types.enum';
import { ButtonTypesEnum } from '../../../../Common/Forms/enums/button-types.enum';
import { CoreDialogService } from '../../../../Core/Dialogs/services/dialog.service';
import { DateQueryModel } from '../../../../Common/Forms/models/date-query.model';
import { DropdownOptionModel } from '../../../../Common/Forms/models/dropdown-option.model';
import { FilterService } from '../../../../Common/Filter/services/abstract-filter.service';
import { FilterTypeModel } from '../../../../Common/Filter/models/abstract-filter-type.model';
import { Mention } from '../../../../Common/Mentions/models/mention';
import { messageSeverityEnum } from '../../../../Common/Misc/enums/message-severity.enum';
import {
    SocialBulkActionEnum, SocialBulkActionSourceMethodEnum, SocialBulkActionSourceTypeEnum
} from '../../../Core/enums/social-bulk-action.enum';

import { FullProfileAnimationModel } from '../../../../Core/Dialogs/models/full-profile-animation.model';
import { FullProfileDialogAnimationsEnum } from '../../../../Core/Dialogs/enums/full-profile-dialog-animations.enum';
import { MonitorConfigService } from '../../../../Common/MonitorShared/services/monitor-config.service';
import { MediaSubscription } from '../../../../Monitor/Shared/models/media-subscription.model';
import { mediaSubTypeToMediaTypeMap } from '@api/mediaSubscriptions/enums/media-subscription-media-type.enum';
import { getMonitoringDateQueryInputTopicOptions } from '../../../../Monitor/Shared/models/monitoring-date-query-input-options';
import { OverlayAnimationStateEnum } from '../../../../Core/Dialogs/enums/overlay-animation.enum';
import { SearchRangeModel } from '../../../../Monitor/Core/models/search-range.model';

import { MentionSearchCriteriaModel } from '../../../../Monitor/Shared/models/mention-search-criteria.model';
import { SearchStatesEnum } from '../../../../Monitor/Shared/enums/search-states.enum';

import { AgentService } from '../../services/agent.service';
import { FilterWidgetComponent } from '../../../../Common/Filter/components/FilterWidget/filter-widget.component';
import { PostExportSetupDialogComponent } from '../../../../Common/Dialogs/components/PostExportSetupDialog/post-export-setup-dialog.component';
import { SocialSearchService } from '../../../Core/services/social-search.service';
import { ThemeService } from '../../services/theme.service';
import { Theme } from '../../models/theme.model';
import { SocialSearchRequestModel } from '../../../../api/mentions/model/social-search-request.model';
import { SocialArchiveSearchService } from '../../services/social-archive-search.service';
import { SocialRoutingService } from '../../../Core/services/social-routing.service';
import { MediaTypeModel } from '../../../../Monitor/Shared/models/media-type.model';
import { MentionSearchQueryModel } from '../../../../Monitor/Shared/models/mention-search-query.model';
import { ExportSearchCallerTypeEnum } from '@Common/MonitorShared/enum/export-search-caller-type.enum';
import { ExportFileTypeEnum } from '@Common/MonitorShared/enum/export-file-type.enum';
import { FileDownloadService } from '@Common/MonitorShared/services/file-downloader.service';
import { MentionTypeEnum } from '@Common/Mentions/components/UnsavedExportInfoMessage/unsaved-export-info-message.component';
import { mediaSubscriptionMediaTypeEnumToSocialMediaTypeApiEnumMap } from '../../../../api/mediaSubscriptions/enums/media-subscription-media-type.enum';


@Component({
    selector: 'agpr-social-bulk-actions-dialog',
    templateUrl: './social-bulk-actions-dialog.component.html',
    styleUrls: ['./social-bulk-actions-dialog.component.scss'],
    providers: [
        SocialSearchService,
        {
            provide: FilterService,
            useExisting: SocialSearchService
        },
        FileDownloadService
    ],
    standalone: false
})
export class SocialBulkActionsDialogComponent extends AbstractDialogComponent implements OnInit, OnDestroy {

    @Input() allSelected: boolean = false;
    @Input() total: number;
    @Input() selectedResults: Mention[] = [];
    @Input() existingDateQuery: DateQueryModel = null;
    @Input() currentTheme: Theme = null;
    @Input() currentThemeId: string;
    @Input() socialSearchCriteria: MentionSearchCriteriaModel;
    @Input() drillDownAgentSegment: boolean;

    selectedSourceMethod: SocialBulkActionSourceMethodEnum;
    selectedSourceType: SocialBulkActionSourceTypeEnum;
    selectedSource: Theme | MediaSubscription;
    selectedBulkAction: SocialBulkActionEnum;
    selectedExportFileType: ExportFileTypeEnum = ExportFileTypeEnum.CSV;

    agentIds: number[] = [];
    actionLabel: string;
    themeOptions: DropdownOptionModel[] = [];
    agentOptions: DropdownOptionModel[] = [];
    sourceOptions: DropdownOptionModel[] = [];
    filters: FilterTypeModel<any>[] = [];
    dateQuery: DateQueryModel = null;
    searchRange: SearchRangeModel = null;
    latestSearchCriteria: MentionSearchCriteriaModel;
    previewCount: number = null;
    isSearchingPosts: boolean = false;
    canDoAction: boolean = false;
    subscriptions: Subscription[] = [];
    overlayContainer: Element;
    combinedQuery: MentionSearchQueryModel = null;

    isArchiveSearch: boolean = false;
    activeArchiveSearch: MediaSubscription;
    enableEngagementInsights: boolean;
    mentionType: MentionTypeEnum = MentionTypeEnum.SOCIAL;

    readonly dialogStyle = AgprComponentStyleEnum;
    readonly buttonTypes = ButtonTypesEnum;
    readonly messageSeverities = messageSeverityEnum;
    readonly agprComponentStyle = AgprComponentStyleEnum;
    readonly socialBulkActionSourceMethods = SocialBulkActionSourceMethodEnum;
    readonly socialBulkActionSourceType = SocialBulkActionSourceTypeEnum;
    readonly socialBulkActions = SocialBulkActionEnum;
    readonly sourceTypeDropdownOptions: DropdownOptionModel[] = [
        new DropdownOptionModel('Social.postFeed.bulkActions.dropdownOptionLabel.theme', SocialBulkActionSourceTypeEnum.THEME),
        new DropdownOptionModel('Social.postFeed.bulkActions.dropdownOptionLabel.agent', SocialBulkActionSourceTypeEnum.AGENT)
    ];
    readonly bulkActionDropdownOptions: DropdownOptionModel[] = [
        new DropdownOptionModel('Social.postFeed.bulkActions.dropdownOptionLabel.export', SocialBulkActionEnum.EXPORT)
    ];
    readonly dateOptions: DropdownOptionModel[] = getMonitoringDateQueryInputTopicOptions();
    readonly postExportOptions: DropdownOptionModel[] = [
        new DropdownOptionModel(
            'Social.postFeed.bulkActions.dropdownOptionLabel.csvExport',
            ExportFileTypeEnum.CSV
        )
    ];
    readonly actionToActionLabelMap: Map<SocialBulkActionEnum, string> = new Map<SocialBulkActionEnum, string>([
        [SocialBulkActionEnum.EXPORT, 'Social.postFeed.bulkActions.actionButtonLabel.next']
    ]);
    private readonly animationTrigger = new BehaviorSubject(
        new FullProfileAnimationModel(
            FullProfileDialogAnimationsEnum.slideInOutAnimation,
            OverlayAnimationStateEnum.open
        )
    );
    readonly EXPORT_LIMIT = 50000;
    unsavedMentionExportLimit: number = 0;
    unsavedMentionQuotaUsage: number = 0;
    remainingUnsavedMentionQuota: number = 0;

    constructor(
        public coreDialogService: CoreDialogService,
        public themeService: ThemeService,
        public agentService: AgentService,
        public socialSearchService: SocialSearchService,
        public filterService: FilterService,
        public monitorConfigService: MonitorConfigService,
        public ref: ElementRef,
        private injector: Injector,
        private socialArchiveSearchService: SocialArchiveSearchService,
        private socialRoutingService: SocialRoutingService,
        public fileDownloadService: FileDownloadService,
    ) {
        super(coreDialogService, 'SocialBulkActions');
        this.setupSubscription();
    }

    ngOnInit(): void {
        this.overlayContainer = document.getElementById('main-content');
        this.selectedSourceMethod = this.selectedResults.length > 0 ? SocialBulkActionSourceMethodEnum.SELECTION : SocialBulkActionSourceMethodEnum.SEARCH;
        if (this.drillDownAgentSegment) {
            this.handleAgentAsSource();
        } else {
            this.selectedSourceType = SocialBulkActionSourceTypeEnum.THEME;
            this.sourceOptions = this.themeOptions;
            this.selectedSource = this.currentTheme;

            // handle drilldown when theme is the source, since we use the agentIds in the query, we have to pass in the theme Id another way
            const themeId = Number(this.currentThemeId);
            if (!this.selectedSource && themeId) {
                this.currentTheme = this.themeOptions.find(option => option.value?.id === themeId)?.value;
                this.selectedSource = this.currentTheme;
            }
            if (this.selectedSource?.subscriptionIds?.length) {
                this.agentIds = this.selectedSource?.subscriptionIds;
            }
        }

        // handle drilldown when agent is the source
        if (!this.selectedSource && (this.socialSearchCriteria?.query?.agent?.include?.length === 1)) {
            this.handleAgentAsSource();
        }

        if (this.isArchiveSearch && this.activeArchiveSearch) {
            this.selectedSourceType = SocialBulkActionSourceTypeEnum.ARCHIVE;
            this.sourceTypeDropdownOptions.push(
                new DropdownOptionModel('Social.postFeed.bulkActions.dropdownOptionLabel.archive', SocialBulkActionSourceTypeEnum.ARCHIVE)
            );
            this.sourceMethodChanged();
        }

        this.dateQuery = DateQueryModel.clone(this.existingDateQuery);
        this.selectedBulkAction = SocialBulkActionEnum.EXPORT;
        this.bulkActionChanged();

        // trigger initial search
        this.sourceMethodChanged();
        this.updateActionButtonState();
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach(subscription => subscription.unsubscribe());
    }

    handleAgentAsSource() {
        this.selectedSourceType = SocialBulkActionSourceTypeEnum.AGENT;
        this.sourceOptions = this.agentOptions;
        if (this.socialSearchCriteria?.query?.agent?.include?.length) {
            const segmentAgentId = this.socialSearchCriteria.query.agent.include[0];
            this.agentOptions.forEach(option => {
                if (option.value.id === segmentAgentId) {
                    this.selectedSource = option.value;
                }
            });

            this.agentIds = [segmentAgentId];
        }
    }

    sourceMethodChanged(): void {
        this.updateActionButtonState();
        if (this.selectedSourceMethod === SocialBulkActionSourceMethodEnum.SELECTION) {
            this.previewCount = this.allSelected ? this.total : this.selectedResults.length;
            if (this.allSelected) {
                this.socialSearchService.setSearchCriteria(this.socialSearchCriteria);
                this.socialSearchService.setAgents(this.socialSearchCriteria.query.agent.include);
                this.socialSearchService.setDateQuery(this.existingDateQuery);
                this.socialSearchService.search(true);
            }
        } else if (this.selectedSourceMethod === SocialBulkActionSourceMethodEnum.SEARCH && this.selectedSource) {
            this.socialSearchService.setSearchCriteria(this.socialSearchCriteria);
            if (this.socialSearchCriteria.filterQuery) {
                this.socialSearchService.populateFiltersFromSearch(this.socialSearchCriteria.filterQuery);
            }
            this.socialSearchService.setAgents(this.agentIds);
            this.socialSearchService.setDateQuery(this.dateQuery);
            this.socialSearchService.search(true);
        } else if (this.isArchiveSearch) {
            this.setArchiveSearchCriteria();
        }
    }

    setArchiveSearchCriteria() {
        const mentionSearchCriteriaModel = new MentionSearchCriteriaModel();
        mentionSearchCriteriaModel.query.socialSearchRequest = new SocialSearchRequestModel();
        mentionSearchCriteriaModel.query.socialSearchRequest.anyTerms = [...this.activeArchiveSearch.keywords.or];
        mentionSearchCriteriaModel.query.socialSearchRequest.mustAllTerms = [...this.activeArchiveSearch.keywords.and];
        mentionSearchCriteriaModel.query.socialSearchRequest.mustNotTerms = [...this.activeArchiveSearch.keywords.not];
        mentionSearchCriteriaModel.query.socialSearchRequest.mediaTypeRequests = [...this.activeArchiveSearch.mediaTypeRequests.map(request => {
            return {
                mediaType: mediaSubscriptionMediaTypeEnumToSocialMediaTypeApiEnumMap.get(request.mediaType),
                handles: request.handles
            };
        })];

        this.activeArchiveSearch.mediaTypes.forEach(type => {
            mentionSearchCriteriaModel.query.includedMediaTypes.push(
                new MediaTypeModel(mediaSubTypeToMediaTypeMap.get(type)));
        });

        this.socialSearchService.setSearchCriteria(mentionSearchCriteriaModel);
        this.socialSearchService.enableEngagementInsights(this.enableEngagementInsights);
        this.socialSearchService.setDateQuery(this.existingDateQuery);
        this.socialSearchService.search(true);
    }

    sourceTypeChanged(): void {
        this.selectedSource = null;
        this.previewCount = null;
        this.sourceOptions = this.selectedSourceType === SocialBulkActionSourceTypeEnum.THEME ? this.themeOptions : this.agentOptions;
        this.updateActionButtonState();
    }

    sourceChanged(): void {
        this.agentIds = this.selectedSource instanceof Theme ? this.selectedSource.subscriptionIds : [ this.selectedSource.id ];
        this.socialSearchService.setAgents(this.agentIds);
        this.socialSearchService.search(true);
        this.updateActionButtonState();
    }

    openFilterWidget(): void {
        const io = {
            inputs: {
                animationTrigger: this.animationTrigger,
                overlayContainer: this.overlayContainer,
                style: AgprComponentStyleEnum.social
            },
            outputs: {}
        };
        this.coreDialogService.open(FilterWidgetComponent, io, this.injector);
    }

    /**
     * updates the date range and re-runs the search
     *
     * @param dateRange
     *
     * @returns void
     */
    dateRangeChanged(dateRange: DateQueryModel): void {
        this.socialSearchService.setDateQuery(dateRange);
        if (this.isArchiveSearch || this.agentIds?.length) {
            this.socialSearchService.search(true);
        } else {
            this.socialSearchService.clearSearch();
            this.socialSearchService.setMentions([]);
        }
    }

    bulkActionChanged(): void {
        this.actionLabel = this.actionToActionLabelMap.get(this.selectedBulkAction);
        this.updateActionButtonState();
    }

    exportFileTypeChanged(): void {
        this.updateActionButtonState();
    }

    updateActionButtonState(): void {
        if (this.selectedSourceMethod === SocialBulkActionSourceMethodEnum.SELECTION
            || (this.selectedSourceMethod === SocialBulkActionSourceMethodEnum.SEARCH && (!!this.selectedSource || this.isArchiveSearch))
        ) {
            if (this.selectedBulkAction === SocialBulkActionEnum.EXPORT && this.selectedExportFileType === ExportFileTypeEnum.CSV) {

                this.canDoAction = this.previewCount <= this.EXPORT_LIMIT || this.previewCount > 0;

                if (this.remainingUnsavedMentionQuota === 0) {
                    this.canDoAction = false;
                }
                if (this.previewCount > this.remainingUnsavedMentionQuota) {
                    this.canDoAction = false;
                }
                if (this.unsavedMentionExportLimit === -1) {
                    this.canDoAction = true;
                }
                if (this.previewCount > this.EXPORT_LIMIT || this.previewCount === 0) {
                    this.canDoAction = false;
                }
            }
        } else {
            this.canDoAction = false;
        }
    }

    doAction(): void {
        if (this.selectedBulkAction === SocialBulkActionEnum.EXPORT) {
            this.openExportSetupDialog();
        }
    }

    openExportSetupDialog(): void {
        let query = null;
        let mercuryIds = [];

        if (this.selectedSourceMethod === SocialBulkActionSourceMethodEnum.SELECTION && !this.allSelected) {
            mercuryIds = this.selectedResults.map(mention => mention.id);
        } else {
            query = this.combinedQuery?.getApiEntity();
        }

        const searchCriteria = this.socialSearchService.generateFilterSearchCriteria();

        const io = {
            inputs: {
                animationTrigger: this.animationTrigger,
                overlayContainer: this.overlayContainer,
                style: AgprComponentStyleEnum.social,
                searchCallerType: ExportSearchCallerTypeEnum.FILE_EXPORT_SOCIAL,
                format: this.selectedExportFileType,
                isSocial: true,
                isUnsavedMentionType: true,
                query: query,
                mercuryIds: mercuryIds,
                searchCriteria: searchCriteria
            },
            outputs: {
                dialogClose: (confirm: boolean) => {
                    if (confirm) {
                        this.closeDialog();
                    }
                }
            }
        };

        this.coreDialogService.open(PostExportSetupDialogComponent, io, this.injector);
    }

    setupSubscription(): void {
        this.subscriptions.push(
            this.themeService.all$.subscribe(themes => {
                this.themeOptions = themes?.map(theme => new DropdownOptionModel(theme.name, theme)) || [];
            }),
            this.agentService.all$.subscribe(agents => {
                this.agentOptions = agents?.map(agent => new DropdownOptionModel(agent.name, agent)) || [];
            }),
            this.socialSearchService.getItemCount().subscribe(count => {
                if (!count && count !== 0) {
                    return;
                }
                this.previewCount = count;
                this.updateActionButtonState();
            }),
            this.socialSearchService.getDateQuery().pipe(filter(x => !!x)).subscribe(dateQuery => {
                this.dateQuery = dateQuery;
            }),
            // search range is for the earliest and latest possible date able to be selected in the date picker
            this.monitorConfigService.getSearchRange().subscribe(searchRange => {
                this.searchRange = searchRange;
            }),
            this.themeService.currentResource$.pipe(filter(x => !!x)).subscribe(theme => {
                this.currentTheme = theme;
                if (theme.subscriptionIds.length > 0) {
                    this.agentIds = theme.subscriptionIds;
                }
            }),
            this.socialSearchService.getLatestSearchCriteria().subscribe(searchCriteria => {
                this.latestSearchCriteria = searchCriteria;
            }),
            this.socialSearchService.getCombinedQuery().subscribe(query => this.combinedQuery = query),
            this.socialSearchService.getSearchState().subscribe(
                searchState => {
                    switch (searchState.type) {
                        case SearchStatesEnum.loading:
                            requestAnimationFrame(() => {
                                this.isSearchingPosts = true;
                            });
                            break;
                        case SearchStatesEnum.success:
                        case SearchStatesEnum.error:
                        default:
                            requestAnimationFrame(() => {
                                this.isSearchingPosts = false;
                                this.updateActionButtonState();
                            });
                    }
                }
            ),
            this.socialRoutingService.isArchiveSearch$.subscribe(x => this.isArchiveSearch = x),
            this.socialArchiveSearchService.activeArchiveSearch$.subscribe(x => this.activeArchiveSearch = x),
            this.socialArchiveSearchService.enableEngagementInsights$.subscribe(x => this.enableEngagementInsights = x),
            this.fileDownloadService.getUnsavedMentionExportLimit().subscribe(limit => {
                this.unsavedMentionExportLimit = limit;
            }),
            this.fileDownloadService.getUnsavedMentionQuotaUsage().subscribe(quotaUsage => {
                this.unsavedMentionQuotaUsage = quotaUsage;
            }),
            this.fileDownloadService.getRemainingUnsavedMentionQuota().subscribe(limit => {
                this.remainingUnsavedMentionQuota = limit;
                this.updateActionButtonState();
            }),
        );
    }
}
